import { AppNavigation as AppNavigationComponent } from 'components'
import { UserRole } from 'gql.generated'
import { useAuthContext } from 'hooks/useAuthContext'
import { translateRoute } from 'i18n/helpers'
import { INavLink, INavLinkGroup, INavProps } from 'office-ui-fabric-react'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRoute } from 'react-router5'
import * as routes from 'routes'

export const AppNavigation = () => {
  const { t } = useTranslation()
  const { route: currentRoute, router } = useRoute()
  const [area, setArea] = useState(
    /^admin/.test(currentRoute.name) ? 'admin' : 'app'
  )
  const auth = useAuthContext()
  const isExternal = auth.hasUserRole(UserRole.Hrg)
  const isReader = auth.hasUserRole(UserRole.Reader)
  const isSysAdmin = auth.hasUserRole(UserRole.SystemAdministrator)
  const isResourceManager = auth.hasUserRole(UserRole.ResourceManager)
  const isUser = auth.hasUserRole(UserRole.User)
  const isProvingCreator = auth.hasUserRole(UserRole.ProvingCreator)
  const isProvingOrganisation = auth.hasUserRole(UserRole.ProvingOrganisation)
  const isServiceTeam = auth.hasUserRole(UserRole.ServiceTeam)
  const isMaintenanceHead = auth.hasUserRole(UserRole.Maintenancehead)

  const modifiedNewsCounter = 0
  const modifiedWikisCounter = 0

  // const { newNewsCount: modifiedNewsCounter } = useNewNewsContext()
  // const { newWikiCount: modifiedWikisCounter } = useNewWikiContext()
  const navigate = React.useMemo(
    () => (ev: any, link?: INavLink) => {
      if (link && link.routeName) {
        router.navigate(link.routeName || link.key, {
          newsCounter: modifiedNewsCounter,
          wikiCounter: modifiedWikisCounter,
        })
      }

      ev.preventDefault()
      ev.stopPropagation()
    },
    [router, modifiedNewsCounter, modifiedWikisCounter]
  )
  useEffect(() => {
    setTimeout(() => {
      const newsDiv = document.querySelector("button[title='Neuigkeiten'] div")
      const alreadyRenderedNews = document.querySelector(
        "button[title='Neuigkeiten'] div div.newsCounter"
      )
      const divForNews = document.createElement('div')
      divForNews.className += 'newsCounter'
      divForNews.innerHTML =
        modifiedNewsCounter === 0 ? '' : `${modifiedNewsCounter} Neu`
      divForNews.setAttribute(
        'style',
        'margin-left: auto; color:rgb(163,0,22);'
      )

      if (newsDiv && !alreadyRenderedNews) {
        newsDiv.appendChild(divForNews)
      }
      if (newsDiv && alreadyRenderedNews) {
        newsDiv.replaceChild(divForNews, alreadyRenderedNews)
      }
    }, 1000)
  }, [modifiedNewsCounter])
  useEffect(() => {
    setTimeout(() => {
      const wikiDiv = document.querySelector("button[title='Wiki'] div")
      const alreadyRendered = document.querySelector(
        "button[title='Wiki'] div div.wikiCounter"
      )
      const div = document.createElement('div')
      div.className += 'wikiCounter'
      div.innerHTML =
        modifiedWikisCounter === 0 ? '' : `${modifiedWikisCounter} Neu`
      div.setAttribute('style', 'margin-left: auto; color:rgb(163,0,22);')
      if (wikiDiv && !alreadyRendered) {
        wikiDiv.appendChild(div)
      }
      if (wikiDiv && alreadyRendered) {
        wikiDiv.replaceChild(div, alreadyRendered)
      }
    }, 1000)
  }, [modifiedWikisCounter])

  const navigateArea = React.useMemo(
    () => (ev: any, link?: INavLink) => {
      if (link) {
        setArea(link.area || link.key)
      }
      ev.preventDefault()
      ev.stopPropagation()
    },
    [setArea]
  )

  const routeNavLink = React.useMemo(
    () =>
      (
        route: routes.Route,
        {
          indexRoute,
          ...props
        }: Partial<INavLink> & { indexRoute?: boolean } = {}
      ): INavLink =>
        indexRoute
          ? {
              key: `${route.name}.index`,
              name: translateRoute(route.name, t),
              url: '',
              routeName: route.name,
              ...props,
            }
          : {
              key: route.name,
              name: translateRoute(route.name, t),
              url: '',
              routeName: route.name,
              links: route.children
                ? route.children.map(({ name, ...childRoute }) =>
                    routeNavLink({
                      name: `${route.name}.${name}`,
                      ...childRoute,
                    })
                  )
                : undefined,
              ...props,
            },
    [t]
  )

  const isExternalGroups = React.useMemo(
    () =>
      ({
        app: [
          {
            links: [
              routeNavLink(routes.Home, {
                iconProps: { iconName: 'Home' },
              }),
              routeNavLink(routes.Todos, {
                iconProps: { iconName: 'Globe' },
              }),
              routeNavLink(routes.Wiki, {
                iconProps: {
                  iconName: 'D365TalentInsight',
                },
                onClick: () => {
                  window.open(
                    'https://skyway.porsche.com/confluence/display/WIKI/EMOS+Wiki+Home',
                    '_blank',
                    'toolbar=0,location=0,menubar=0'
                  )
                },
              }),
              routeNavLink(routes.Mailbox, {
                iconProps: { iconName: 'Inbox' },
              }),
            ],
          },
        ] as INavLinkGroup[],
      }[area]),
    [area, routeNavLink]
  )

  const isUserGroups = React.useMemo(
    () =>
      ({
        app: [
          {
            links: [
              routeNavLink(routes.Home, {
                iconProps: { iconName: 'Home' },
              }),
              routeNavLink(routes.Todos, {
                iconProps: { iconName: 'Globe' },
              }),
              routeNavLink(routes.MyProvings, {
                iconProps: { iconName: 'TestBeaker' },
              }),
              routeNavLink(routes.MyBookings, {
                iconProps: { iconName: 'ThumbnailView' },
              }),
              routeNavLink(routes.BookingManager, {
                isExpanded: false,
                links: routeNavLink(routes.BookingManager)
                  .links!.filter(
                    (link) =>
                      link.routeName !== 'bookingManager.bookingsAppletView'
                  )
                  .map((link) => ({
                    ...link,
                    routeName: link.links ? undefined : link.routeName,
                  })),
              }),
              routeNavLink(routes.Synergies, {
                iconProps: { iconName: 'Sync' },
              }),
              routeNavLink(routes.Wiki, {
                iconProps: {
                  iconName: 'D365TalentInsight',
                },
                onClick: () => {
                  window.open(
                    'https://skyway.porsche.com/confluence/display/WIKI/EMOS+Wiki+Home',
                    '_blank',
                    'toolbar=0,location=0,menubar=0'
                  )
                },
              }),
              routeNavLink(routes.News, {
                iconProps: { iconName: 'Globe' },
              }),
            ],
          },
        ] as INavLinkGroup[],
      }[area]),
    [area, routeNavLink]
  )

  const isReaderGroups = React.useMemo(
    () =>
      ({
        app: [
          {
            links: [
              routeNavLink(routes.Home, {
                iconProps: { iconName: 'Home' },
              }),
              routeNavLink(routes.Todos, {
                iconProps: { iconName: 'Globe' },
              }),
              routeNavLink(routes.MyProvings, {
                iconProps: { iconName: 'TestBeaker' },
              }),
              routeNavLink(routes.MyBookings, {
                iconProps: { iconName: 'ThumbnailView' },
              }),
              routeNavLink(routes.BookingManager, {
                isExpanded: false,
                links: routeNavLink(routes.BookingManager).links!.map(
                  (link) => ({
                    ...link,
                    routeName: link.links ? undefined : link.routeName,
                  })
                ),
              }),
              routeNavLink(routes.Synergies, {
                iconProps: { iconName: 'Sync' },
              }),
              routeNavLink(routes.Provings, {
                indexRoute: true,
                isExpanded: true,
                links: routeNavLink(routes.Provings)
                  .links!.filter(
                    (link) =>
                      link.routeName !== 'provings.index' &&
                      link.routeName !== 'provings.details'
                  )
                  .map((link: any) => {
                    if (
                      link &&
                      link.routeName !== 'provings.index' &&
                      link &&
                      link.routeName !== 'provings.details'
                    ) {
                      return {
                        ...link,
                        routeName: link.links ? undefined : link.routeName,
                      }
                    }
                    return undefined
                  }),
              }),
              routeNavLink(routes.AssignedWorker, {
                iconProps: { iconName: 'WorkforceManagement' },
              }),
              // routeNavLink(routes.ProvingsByMaintenanceHead, {
              //   iconProps: { iconName: 'ManagerSelfService' },
              // }),
              routeNavLink(routes.Participants, {
                indexRoute: true,
                isExpanded: true,
                links: routeNavLink(routes.Participants).links!.map((link) => ({
                  ...link,
                  routeName: link.links ? undefined : link.routeName,
                })),
              }),
              routeNavLink(routes.ResourceBookings, {
                isExpanded: false,
                links: routeNavLink(routes.ResourceBookings).links!.map(
                  (link) => ({
                    ...link,
                    routeName: link.links ? undefined : link.routeName,
                  })
                ),
              }),
              routeNavLink(routes.Persons, {
                isExpanded: false,
                links: routeNavLink(routes.Persons).links!.map((link) => ({
                  ...link,
                  routeName: link.links ? undefined : link.routeName,
                })),
              }),
              routeNavLink(routes.Wiki, {
                iconProps: {
                  iconName: 'D365TalentInsight',
                },
                onClick: () => {
                  window.open(
                    'https://skyway.porsche.com/confluence/display/WIKI/EMOS+Wiki+Home',
                    '_blank',
                    'toolbar=0,location=0,menubar=0'
                  )
                },
              }),
              routeNavLink(routes.News, {
                iconProps: { iconName: 'Globe' },
              }),
              routeNavLink(routes.Archives, {
                indexRoute: true,
                isExpanded: false,
                links: routeNavLink(routes.Archives).links!.map((link: any) => {
                  return {
                    ...link,
                    routeName: link.links ? undefined : link.routeName,
                  }
                }),
              }),
              // ...(auth.hasUserRole(UserRole.SystemAdministrator) ||
              // auth.hasUserRole(UserRole.ResourceManager)
              //   ? [
              //       routeNavLink(routes.TaxRequirements, {
              //         iconProps: { iconName: 'AllCurrency' },
              //       }),
              //     ]
              //   : []),
            ],
          },
        ] as INavLinkGroup[],
      }[area]),
    [area, routeNavLink]
  )

  const isMaintenanceHeadGroups = React.useMemo(
    () =>
      ({
        app: [
          {
            links: [
              routeNavLink(routes.Home, {
                iconProps: { iconName: 'Home' },
              }),
              routeNavLink(routes.Todos, {
                iconProps: { iconName: 'Globe' },
              }),
              routeNavLink(routes.MyProvings, {
                iconProps: { iconName: 'TestBeaker' },
              }),
              routeNavLink(routes.MyBookings, {
                iconProps: { iconName: 'ThumbnailView' },
              }),
              routeNavLink(routes.BookingManager, {
                isExpanded: false,
                links: routeNavLink(routes.BookingManager)
                  .links!.filter(
                    (link) =>
                      link.routeName !== 'bookingManager.bookingsAppletView'
                  )
                  .map((link) => ({
                    ...link,
                    routeName: link.links ? undefined : link.routeName,
                  })),
              }),
              routeNavLink(routes.Provings, {
                indexRoute: true,
                isExpanded: true,
                links: routeNavLink(routes.Provings)
                  .links!.filter(
                    (link) => link.routeName === 'provings.provingsDate'
                  )
                  .map((link: any) => {
                    if (link && link.routeName === 'provings.provingsDate') {
                      return {
                        ...link,
                        routeName: link.links ? undefined : link.routeName,
                      }
                    }
                    return undefined
                  }),
              }),
              routeNavLink(routes.AssignedWorker, {
                iconProps: { iconName: 'WorkforceManagement' },
              }),
              routeNavLink(routes.Archives, {
                indexRoute: true,
                isExpanded: false,
                links: routeNavLink(routes.Archives).links!.map((link: any) => {
                  return {
                    ...link,
                    routeName: link.links ? undefined : link.routeName,
                  }
                }),
              }),
              // routeNavLink(routes.ProvingsByMaintenanceHead, {
              //   iconProps: { iconName: 'ManagerSelfService' },
              // }),
              routeNavLink(routes.Wiki, {
                iconProps: {
                  iconName: 'D365TalentInsight',
                },
                onClick: () => {
                  window.open(
                    'https://skyway.porsche.com/confluence/display/WIKI/EMOS+Wiki+Home',
                    '_blank',
                    'toolbar=0,location=0,menubar=0'
                  )
                },
              }),
              routeNavLink(routes.News, {
                iconProps: { iconName: 'Globe' },
              }),
            ],
          },
        ] as INavLinkGroup[],
      }[area]),
    [area, routeNavLink]
  )

  const isServiceTeamGroups = React.useMemo(
    () =>
      ({
        app: [
          {
            links: [
              routeNavLink(routes.Home, {
                iconProps: { iconName: 'Home' },
              }),
              routeNavLink(routes.Todos, {
                iconProps: { iconName: 'Globe' },
              }),
              routeNavLink(routes.MyProvings, {
                iconProps: { iconName: 'TestBeaker' },
              }),
              routeNavLink(routes.MyBookings, {
                iconProps: { iconName: 'ThumbnailView' },
              }),
              routeNavLink(routes.BookingManager, {
                isExpanded: false,
                links: routeNavLink(routes.BookingManager).links!.map(
                  (link) => ({
                    ...link,
                    routeName: link.links ? undefined : link.routeName,
                  })
                ),
              }),
              routeNavLink(routes.Mailbox, {
                iconProps: { iconName: 'Inbox' },
              }),
              routeNavLink(routes.Provings, {
                indexRoute: true,
                isExpanded: true,
                links: routeNavLink(routes.Provings)
                  .links!.filter(
                    (link) =>
                      link.routeName !== 'provings.index' &&
                      link.routeName !== 'provings.details'
                  )
                  .map((link: any) => {
                    if (
                      link &&
                      link.routeName !== 'provings.index' &&
                      link &&
                      link.routeName !== 'provings.details'
                    ) {
                      return {
                        ...link,
                        routeName: link.links ? undefined : link.routeName,
                      }
                    }
                    return undefined
                  }),
              }),
              routeNavLink(routes.AssignedWorker, {
                iconProps: { iconName: 'WorkforceManagement' },
              }),
              // routeNavLink(routes.ProvingsByMaintenanceHead, {
              //   iconProps: { iconName: 'ManagerSelfService' },
              // }),
              routeNavLink(routes.Participants, {
                indexRoute: true,
                isExpanded: true,
                links: routeNavLink(routes.Participants).links!.map((link) => ({
                  ...link,
                  routeName: link.links ? undefined : link.routeName,
                })),
              }),
              routeNavLink(routes.Wiki, {
                iconProps: {
                  iconName: 'D365TalentInsight',
                },
                onClick: () => {
                  window.open(
                    'https://skyway.porsche.com/confluence/display/WIKI/EMOS+Wiki+Home',
                    '_blank',
                    'toolbar=0,location=0,menubar=0'
                  )
                },
              }),
              routeNavLink(routes.News, {
                iconProps: { iconName: 'Globe' },
              }),
              routeNavLink(routes.Archives, {
                indexRoute: true,
                isExpanded: false,
                links: routeNavLink(routes.Archives).links!.map((link: any) => {
                  return {
                    ...link,
                    routeName: link.links ? undefined : link.routeName,
                  }
                }),
              }),
            ],
          },
        ] as INavLinkGroup[],
      }[area]),
    [area, routeNavLink]
  )

  const isProvingOrganisationGroups = React.useMemo(
    () =>
      ({
        app: [
          {
            links: [
              routeNavLink(routes.Home, {
                iconProps: { iconName: 'Home' },
              }),
              routeNavLink(routes.Todos, {
                iconProps: { iconName: 'Globe' },
              }),
              routeNavLink(routes.MyProvings, {
                iconProps: { iconName: 'TestBeaker' },
              }),
              routeNavLink(routes.MyBookings, {
                iconProps: { iconName: 'ThumbnailView' },
              }),
              routeNavLink(routes.BookingManager, {
                isExpanded: false,
                links: routeNavLink(routes.BookingManager).links!.map(
                  (link) => ({
                    ...link,
                    routeName: link.links ? undefined : link.routeName,
                  })
                ),
              }),
              routeNavLink(routes.Synergies, {
                iconProps: { iconName: 'Sync' },
              }),
              routeNavLink(routes.Mailbox, {
                iconProps: { iconName: 'Inbox' },
              }),
              routeNavLink(routes.Provings, {
                indexRoute: true,
                isExpanded: true,
                links: routeNavLink(routes.Provings)
                  .links!.filter(
                    (link) =>
                      link.routeName !== 'provings.index' &&
                      link.routeName !== 'provings.details'
                  )
                  .map((link: any) => {
                    if (
                      link &&
                      link.routeName !== 'provings.index' &&
                      link &&
                      link.routeName !== 'provings.details'
                    ) {
                      return {
                        ...link,
                        routeName: link.links ? undefined : link.routeName,
                      }
                    }
                    return undefined
                  }),
              }),
              routeNavLink(routes.AssignedWorker, {
                iconProps: { iconName: 'WorkforceManagement' },
              }),
              // routeNavLink(routes.ProvingsByMaintenanceHead, {
              //   iconProps: { iconName: 'ManagerSelfService' },
              // }),
              routeNavLink(routes.Participants, {
                indexRoute: true,
                isExpanded: true,
                links: routeNavLink(routes.Participants).links!.map((link) => ({
                  ...link,
                  routeName: link.links ? undefined : link.routeName,
                })),
              }),
              routeNavLink(routes.Wiki, {
                iconProps: {
                  iconName: 'D365TalentInsight',
                },
                onClick: () => {
                  window.open(
                    'https://skyway.porsche.com/confluence/display/WIKI/EMOS+Wiki+Home',
                    '_blank',
                    'toolbar=0,location=0,menubar=0'
                  )
                },
              }),
              routeNavLink(routes.News, {
                iconProps: { iconName: 'Globe' },
              }),
              routeNavLink(routes.Archives, {
                indexRoute: true,
                isExpanded: false,
                links: routeNavLink(routes.Archives).links!.map((link: any) => {
                  return {
                    ...link,
                    routeName: link.links ? undefined : link.routeName,
                  }
                }),
              }),
            ],
          },
        ] as INavLinkGroup[],
      }[area]),
    [area, routeNavLink]
  )

  const groups = React.useMemo(
    () =>
      ({
        app: [
          {
            links: [
              routeNavLink(routes.Home, {
                iconProps: { iconName: 'Home' },
              }),
              routeNavLink(routes.Todos, {
                iconProps: { iconName: 'Globe' },
              }),
              routeNavLink(routes.MyProvings, {
                iconProps: { iconName: 'TestBeaker' },
              }),
              routeNavLink(routes.MyBookings, {
                iconProps: { iconName: 'ThumbnailView' },
              }),
              routeNavLink(routes.BookingManager, {
                isExpanded: false,
                links: routeNavLink(routes.BookingManager).links!.map(
                  (link) => ({
                    ...link,
                    routeName: link.links ? undefined : link.routeName,
                  })
                ),
              }),
              routeNavLink(routes.Synergies, {
                iconProps: { iconName: 'Sync' },
              }),
              routeNavLink(routes.Mailbox, {
                iconProps: { iconName: 'Inbox' },
              }),
              routeNavLink(routes.Provings, {
                indexRoute: true,
                isExpanded: true,
                links: routeNavLink(routes.Provings)
                  .links!.filter(
                    (link) =>
                      link.routeName !== 'provings.index' &&
                      link.routeName !== 'provings.details'
                  )
                  .map((link: any) => {
                    if (
                      link &&
                      link.routeName !== 'provings.index' &&
                      link &&
                      link.routeName !== 'provings.details'
                    ) {
                      return {
                        ...link,
                        routeName: link.links ? undefined : link.routeName,
                      }
                    }
                    return undefined
                  }),
              }),
              routeNavLink(routes.AssignedWorker, {
                iconProps: { iconName: 'WorkforceManagement' },
              }),
              // routeNavLink(routes.ProvingsByMaintenanceHead, {
              //   iconProps: { iconName: 'ManagerSelfService' },
              // }),
              routeNavLink(routes.Participants, {
                indexRoute: true,
                isExpanded: true,
                links: routeNavLink(routes.Participants).links!.map((link) => ({
                  ...link,
                  routeName: link.links ? undefined : link.routeName,
                })),
              }),
              routeNavLink(routes.ResourceBookings, {
                isExpanded: false,
                links: routeNavLink(routes.ResourceBookings).links!.map(
                  (link) => ({
                    ...link,
                    routeName: link.links ? undefined : link.routeName,
                  })
                ),
              }),
              routeNavLink(routes.Persons, {
                isExpanded: false,
                links: routeNavLink(routes.Persons).links!.map((link) => ({
                  ...link,
                  routeName: link.links ? undefined : link.routeName,
                })),
              }),
              routeNavLink(routes.Wiki, {
                iconProps: {
                  iconName: 'D365TalentInsight',
                },
                onClick: () => {
                  window.open(
                    'https://skyway.porsche.com/confluence/display/WIKI/EMOS+Wiki+Home',
                    '_blank',
                    'toolbar=0,location=0,menubar=0'
                  )
                },
              }),
              routeNavLink(routes.News, {
                iconProps: { iconName: 'Globe' },
              }),
              routeNavLink(routes.Archives, {
                indexRoute: true,
                isExpanded: false,
                links: routeNavLink(routes.Archives).links!.map((link: any) => {
                  return {
                    ...link,
                    routeName: link.links ? undefined : link.routeName,
                  }
                }),
              }),
              // ...(auth.hasUserRole(UserRole.SystemAdministrator) ||
              // auth.hasUserRole(UserRole.ResourceManager)
              //   ? [
              //       routeNavLink(routes.TaxRequirements, {
              //         iconProps: { iconName: 'AllCurrency' },
              //       }),
              //     ]
              //   : []),
            ],
          },
        ] as INavLinkGroup[],
        admin: [
          {
            collapseByDefault: false,
            links: routeNavLink(routes.Admin)
              .links!.filter((link) => {
                if (isResourceManager) {
                  return (
                    link.key !==
                    `${routes.Admin.name}.${routes.AdminSettings.name}`
                  )
                }
                if (isSysAdmin) {
                  return true
                }
                return false
              })
              .map((link) => ({
                ...link,
                routeName: link.links ? undefined : link.routeName,
                isExpanded: true,
              })),
          },
        ] as INavLinkGroup[],
      }[area]),
    [area, routeNavLink, isResourceManager, isSysAdmin]
  )

  const noRights = React.useMemo(
    () =>
      ({
        app: [
          {
            links: [
              routeNavLink(routes.Home, {
                iconProps: { iconName: 'Home' },
              }),
            ],
          },
        ] as INavLinkGroup[],
      }[area]),
    [routeNavLink, area]
  )

  const navProps: INavProps = {
    onLinkClick: navigate,
    selectedKey: currentRoute.name,
    groups: isExternal
      ? isExternalGroups
      : isReader
      ? isReaderGroups
      : (isUser || isProvingCreator)
      ? isUserGroups
      : isServiceTeam
      ? isServiceTeamGroups
      : isProvingOrganisation
      ? isProvingOrganisationGroups
      : isMaintenanceHead
      ? isMaintenanceHeadGroups
      : isSysAdmin
      ? groups
      : isResourceManager
      ? groups
      : noRights,
  }

  const areaNavProps: INavProps | undefined =
    isSysAdmin || isResourceManager
      ? {
          onLinkClick: navigateArea,
          selectedKey: area,
          groups: [
            {
              links: [
                {
                  name: '',
                  key: 'app',
                  url: '',
                  iconProps: { iconName: 'Work' },
                },
                {
                  name: '',
                  key: 'admin',
                  url: '',
                  iconProps: { iconName: 'AdminALogoInverse32' },
                },
              ],
            },
          ],
        }
      : undefined

  return (
    <AppNavigationComponent navProps={navProps} areaNavProps={areaNavProps} />
  )
}
